import * as THREE from 'three';
import type { SkillEffect } from './skill-effects.js';

const DURATION = 1.8, SMOKE = 0x8b8274, COUNT = 11;
/** A smaller, darker or quicker cloud for other abilities (the Sneak smoke bomb). */
export interface SmokeOptions { seconds?: number; color?: number; size?: number; name?: string }
const ease = (p: number) => 1 - (1 - p) ** 3;

/** One lit, billowing lump of smoke: Lambert shading takes the scene's light and fog; its rim thins to nothing. */
function smokeMaterial(seed: number, uniforms: { uTime: { value: number } }, color = SMOKE) {
  const material = new THREE.MeshLambertMaterial({ color: new THREE.Color(color).offsetHSL(0, 0, (seed - .5) * .08), transparent: true, depthWrite: false, opacity: 0 });
  material.name = 'smoke-puff';
  material.onBeforeCompile = shader => {
    shader.uniforms.uTime = uniforms.uTime; shader.uniforms.uSeed = { value: seed };
    shader.vertexShader = shader.vertexShader.replace('#include <common>', '#include <common>\nuniform float uTime;\nuniform float uSeed;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        float billow = sin(dot(position, vec3(3.1, 2.3, 2.7)) + uSeed * 6.3 + uTime * 1.4) * .55
          + sin(dot(position, vec3(-2.2, 3.7, 1.4)) * 1.7 + uSeed * 11.0 - uTime * 1.1) * .3;
        transformed *= 1.0 + .17 * billow;`);
    shader.fragmentShader = shader.fragmentShader.replace('#include <opaque_fragment>', `#include <opaque_fragment>
        gl_FragColor.a *= smoothstep(.04, .65, abs(dot(normalize(normal), normalize(vViewPosition))));`);
  };
  material.customProgramCacheKey = () => 'smoke-puff';
  return material;
}

/** Get In Here!: an opaque-ish grey-brown cloud bursts where the minion appears, then rolls outward and thins to reveal it. */
export function smokePuff(height = 1.4, radius = .4, options: SmokeOptions = {}): SkillEffect {
  const group = new THREE.Group(); group.name = options.name ?? 'get-in-here-smoke';
  const seconds = options.seconds ?? DURATION;
  const uniforms = { uTime: { value: 0 } }, geometry = new THREE.IcosahedronGeometry(1, 3), size = Math.max(.32, height * .3) * (options.size ?? 1);
  const puffs = Array.from({ length: COUNT }, (_, i) => {
    const seed = (i * .61803398875) % 1, core = i < 4, angle = i * 2.39996 + seed;
    const mesh = new THREE.Mesh(geometry, smokeMaterial(seed, uniforms, options.color)); mesh.name = 'smoke-lump'; group.add(mesh);
    return { mesh, seed, core, dir: new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle)),
      start: core ? radius * .3 : radius * .6, spread: core ? radius + .25 : radius * 1.6 + .55,
      lift: core ? height * (.25 + i * .14) : height * (.06 + seed * .2), rise: core ? height * .45 : height * (.15 + seed * .25),
      scale: size * (core ? 1.1 + seed * .3 : .75 + seed * .35), squash: core ? 1 : .7 };
  });
  let spawn: THREE.Vector3 | undefined;
  return { group, duration: seconds * 1000, update(p, _from, to, time = p * seconds) {
    // The cloud stays where the minion appeared, however soon it walks out of it.
    spawn ??= to.clone(); group.position.copy(spawn);
    uniforms.uTime.value = time;
    const out = ease(p);
    for (const puff of puffs) {
      const grow = .45 + .85 * out, at = puff.start + puff.spread * out;
      puff.mesh.position.set(puff.dir.x * at, puff.lift + puff.rise * out, puff.dir.z * at);
      puff.mesh.scale.set(puff.scale * grow, puff.scale * grow * puff.squash, puff.scale * grow);
      puff.mesh.rotation.y = puff.seed * 6 + time * (puff.seed - .5) * .6;
      // Dense at once, the core clears first so the minion steps out of the smoke.
      const thin = puff.core ? THREE.MathUtils.smoothstep(p, .18, .7) : THREE.MathUtils.smoothstep(p, .3 + puff.seed * .15, 1);
      (puff.mesh.material as THREE.MeshLambertMaterial).opacity = .82 * Math.min(1, p / .05) * (1 - thin);
    }
  } };
}
